import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import api from "@/lib/api";
import { ThumbsUp, ThumbsDown, Minus, Clock, ChevronRight } from "lucide-react";

function VerdictBadge({ verdict }) {
    const map = {
        up: {
            bg: "bg-[#3B824E]/10",
            color: "#3B824E",
            Icon: ThumbsUp,
            label: "Good fit",
        },
        sideways: {
            bg: "bg-[#C47A55]/10",
            color: "#C47A55",
            Icon: Minus,
            label: "Mixed",
        },
        down: {
            bg: "bg-[#AD4A42]/10",
            color: "#AD4A42",
            Icon: ThumbsDown,
            label: "Skip",
        },
    };
    const v = map[verdict] || map.sideways;
    const { Icon } = v;
    return (
        <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${v.bg}`}
            style={{ color: v.color }}
        >
            <Icon className="w-3.5 h-3.5" />
            <span className="text-[11px] font-medium">{v.label}</span>
        </div>
    );
}

export default function History() {
    const [items, setItems] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/history");
                setItems(r.data);
            } catch {
                setItems([]);
            }
        })();
    }, []);

    return (
        <Shell>
            <div className="nl-fade-up">
                <p className="font-eyebrow mb-2">Archive</p>
                <h1
                    className="font-serif-editorial text-4xl sm:text-5xl leading-[1.02] tracking-tight"
                    style={{ color: "#1A3626" }}
                >
                    Your past
                    <br />
                    <em className="italic font-normal">scans.</em>
                </h1>

                <div className="mt-8 space-y-3" data-testid="history-list">
                    {items === null && (
                        <div className="nl-card text-[#8BA092] text-sm">Loading…</div>
                    )}
                    {items && items.length === 0 && (
                        <div className="nl-card text-center py-10" data-testid="history-empty">
                            <Clock
                                className="w-8 h-8 mx-auto mb-3"
                                style={{ color: "#8BA092" }}
                            />
                            <p className="font-serif-editorial text-xl text-[#1A3626]">
                                Nothing scanned yet
                            </p>
                            <p className="text-sm text-[#4A5A4E] mt-1">
                                Your analyses will show up here.
                            </p>
                            <button
                                className="mt-5 nl-btn-primary"
                                onClick={() => navigate("/dashboard")}
                                data-testid="history-empty-cta"
                            >
                                Scan your first item
                            </button>
                        </div>
                    )}

                    {items?.map((it) => (
                        <Link
                            key={it.analysis_id}
                            to={`/history/${it.analysis_id}`}
                            data-testid={`history-item-${it.analysis_id}`}
                            className="block nl-card hover:-translate-y-0.5 transition-transform"
                        >
                            <div className="flex items-start gap-4">
                                <div
                                    className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                                    style={{
                                        background: "#EAF0E9",
                                    }}
                                >
                                    <span
                                        className="font-serif-editorial text-2xl"
                                        style={{ color: "#1A3626" }}
                                    >
                                        {it.score}
                                    </span>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <h3
                                            className="font-medium truncate"
                                            style={{ color: "#1A3626" }}
                                        >
                                            {it.product_name}
                                        </h3>
                                        <VerdictBadge verdict={it.verdict} />
                                    </div>
                                    <p className="text-sm text-[#4A5A4E] line-clamp-2">
                                        {it.summary}
                                    </p>
                                    <p className="text-[11px] text-[#8BA092] mt-1">
                                        {new Date(it.created_at).toLocaleString()}
                                    </p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-[#8BA092] mt-2" />
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </Shell>
    );
}
